/**
 * Brok agent — welcome strings and seed constants.
 * [Source: Story P1b spec §4]
 * UI-U10: + the Heute-Briefing decision sentence + count helpers.
 */
import type { AgentSseEvent, PendingWorkSummary } from '@brokly/shared';

// re-export so callers can import AgentSseEvent type from here without direct shared dep
export type { AgentSseEvent };

/** Time-of-day greeting from the caller's local clock. */
export function tageszeitGruss(date: Date = new Date()): 'Guten Morgen' | 'Guten Tag' | 'Guten Abend' {
  const h = date.getHours();
  if (h >= 5 && h < 11) return 'Guten Morgen';
  if (h >= 11 && h < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/** Greeting line: time-of-day phrase + optional Vorname (neutral when null). */
export function greeting(vorname: string | null, now: Date = new Date()): string {
  const gruss = tageszeitGruss(now);
  return vorname ? `${gruss}, ${vorname}.` : `${gruss}.`;
}

/** First token of the session display name (the informal Vorname), or null. */
export function vornameFromDisplayName(displayName: string | null | undefined): string | null {
  const first = displayName?.trim().split(/\s+/)[0];
  return first || null;
}

/**
 * Seed turn auto-fired once on an empty thread so Brok speaks first with a grounded summary
 * (server loads the lazy pending-work summary + streams it back). Hidden from the transcript.
 */
export const BROK_KICKOFF_PROMPT = 'Was steht heute an?' as const;

export const SUGGESTION_CHIPS = [
  { id: 'rechnungen', label: 'Rechnungen prüfen' },
  { id: 'renewal', label: 'Renewal ansehen' },
  { id: 'sonst', label: 'Was steht sonst an?' },
] as const;

export const BROK_INPUT_PLACEHOLDER = 'Frag mich, was ansteht …' as const;

// ── UI-U10: Heute-Briefing helpers ────────────────────────────────────────────

/** Footer link that opens the full persisted thread at /brok (Briefing → BrokScreen). */
export const BRIEFING_OPEN_FULL_LABEL = 'Ganze Unterhaltung öffnen' as const;

/** German number words for the small counts the decision sentence deals with. */
const ZAHLWORT = [
  'null', 'ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs',
  'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf',
] as const;

/**
 * The Briefing's opening line under the greeting (mirrors the prototype's
 * «… Dinge brauchen heute deinen Entscheid. Womit möchtest du starten?»).
 *
 * `count` is how many Entscheid categories currently need attention (see
 * `briefingDecisionCount`). `null` → the summary is unknown (endpoint absent) →
 * a neutral opener with no fabricated number.
 */
export function decisionSentence(count: number | null): string {
  if (count === null) return 'Womit möchtest du heute starten?';
  if (count <= 0) return 'Aktuell braucht nichts deinen Entscheid. Womit möchtest du starten?';
  const word = count < ZAHLWORT.length ? ZAHLWORT[count] : String(count);
  if (count === 1) {
    return 'Ein Punkt braucht heute deinen Entscheid. Womit möchtest du starten?';
  }
  const Word = `${word![0]!.toUpperCase()}${word!.slice(1)}`;
  return `${Word} Punkte brauchen heute deinen Entscheid. Womit möchtest du starten?`;
}

/**
 * How many of the count-bearing Entscheid categories are currently non-empty
 * (invoices awaiting review · red renewals · open tasks). `null` when there is no
 * summary at all (the Briefing then shows tiles without counts + the neutral opener).
 */
export function briefingDecisionCount(summary: PendingWorkSummary | null): number | null {
  if (!summary) return null;
  let n = 0;
  if ((summary.invoices?.awaitingCount ?? 0) > 0) n++;
  if ((summary.renewals?.red ?? 0) > 0) n++;
  if ((summary.tasks?.count ?? 0) > 0) n++;
  return n;
}
